import {
  ConfigurationItem,
  DisplayResultMessage,
  OrderTypes,
} from "@shared/types";
import {
  getChildren,
  getItemValue,
  getItemValueType,
} from "@shared/features/configuration/utils";
import { cloneDeep } from "lodash";
import { ORDER_TYPE_TO_BUY_OR_SELL } from "@shared/features/configuration/constants";
import { isSymbol } from "@shared/types/utils";
import { sharedLib } from "@shared/services/shared.service";
import { BotStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotStrategies.context";
import { useContext, useEffect } from "react";

interface useSharedCreateCustomStrategyProps {
  updatedName: string;
  customStrategiesItem?: ConfigurationItem;
  customStrategyTemplate?: ConfigurationItem;
  accountId: number;
  isSignal?: boolean;
  subGroupIds?: number[];
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  itemToEdit?: ConfigurationItem;
  setItemToEdit?: (item: ConfigurationItem | undefined) => void;
  resetCustomStrategyTargets?: () => void;
  useCustomStrategyEndpoints?: boolean;
  onSave: () => void;
  targets?: number[];
  displayResultMessage: DisplayResultMessage;
  handleSelectItem?: (item: ConfigurationItem) => void;
}

const useSharedCreateCustomStrategy = ({
  updatedName,
  customStrategiesItem,
  customStrategyTemplate,
  accountId,
  isSignal,
  subGroupIds,
  orderType,
  itemToEdit,
  setItemToEdit,
  resetCustomStrategyTargets,
  useCustomStrategyEndpoints,
  onSave,
  targets,
  displayResultMessage,
  handleSelectItem,
}: useSharedCreateCustomStrategyProps) => {
  const { isCustomStrategyValid, targetsData, createCustomStrategyTemplate } =
    useContext(BotStrategiesContext)!;
  const subGroupId = subGroupIds?.[0];

  const [
    createSubGroupCustomStrategy,
    { isLoading: createSubGroupCustomStrategyLoading },
  ] =
    sharedLib.sharedApi.configuration.useCreateSubGroupCustomStrategyMutation();
  const [createCustomStrategy, { isLoading: createCustomStrategyLoading }] =
    sharedLib.sharedApi.configuration.useCreateCustomStrategyMutation();
  const [editCustomStrategy, { isLoading: editCustomStrategyLoading }] =
    sharedLib.sharedApi.configuration.useEditCustomStrategyMutation();

  useEffect(() => {
    if (targets) targetsData.setTargets(targets);
  }, [targets]);

  const setItemNameAndTargets = (
    innerItem: ConfigurationItem,
    targets: number[],
    strategyId?: number
  ) => {
    const itemValue = getItemValue(innerItem);
    if (strategyId) itemValue["type"] = strategyId;
    itemValue["targets"] = [...targets, ...Array(10 - targets.length).fill(0)];
    innerItem["name"][0]["outcome"] = updatedName;
  };

  const getNewStrategyItem = (targets: number[], strategyId?: number) => {
    const newItem = cloneDeep(createCustomStrategyTemplate);
    if (newItem) {
      setItemNameAndTargets(newItem, targets, strategyId);
    }
    return newItem;
  };

  const updateCustomStrategies = (
    updatedTargets: number[],
    strategyId?: number
  ) => {
    const customStrategies = getChildren(customStrategiesItem);
    const newStrategyItem = getNewStrategyItem(updatedTargets, strategyId);
    customStrategies.push(newStrategyItem);
    if (newStrategyItem) handleSelectItem?.(newStrategyItem);
  };

  const handleCallEndpoint = (updatedTargets: number[]) => {
    const baseArgs = {
      name: updatedName,
      ratios: updatedTargets.map((target) => target / 100),
    };

    let endpoint: any;
    if (isSignal) {
      if (!subGroupId) return;
      const args = {
        ...baseArgs,
        sub_group_id: subGroupId,
        buy_or_sell: ORDER_TYPE_TO_BUY_OR_SELL[orderType],
      };
      endpoint = createSubGroupCustomStrategy(args);
    } else {
      const args = {
        ...baseArgs,
        client_id: accountId,
      };
      endpoint = itemToEdit
        ? editCustomStrategy({ ...args, type: getItemValueType(itemToEdit) })
        : createCustomStrategy({
            ...args,
            buy_or_sell: ORDER_TYPE_TO_BUY_OR_SELL[orderType],
          });
    }

    displayResultMessage(endpoint.unwrap())
      .then((response) => {
        if (!!itemToEdit && !!createCustomStrategyTemplate) {
          setItemNameAndTargets(createCustomStrategyTemplate, updatedTargets);
        } else {
          const strategyId = response?.data?.data?.type;
          updateCustomStrategies(updatedTargets, strategyId);
        }
        onSaveStrategy();
      })
      .catch(() => {});
  };

  const onSaveStrategy = () => {
    setItemToEdit?.(undefined);
    resetCustomStrategyTargets?.();
    onSave();
  };

  const handleSaveItem = () => {
    if (
      !isCustomStrategyValid ||
      !targetsData.targets ||
      isSymbol(targetsData.targets)
    )
      return;
    const updatedTargets = [
      ...targetsData.targets,
      ...Array(10 - targetsData.targets.length).fill(0),
    ];
    if (useCustomStrategyEndpoints) {
      handleCallEndpoint(updatedTargets);
    } else {
      if (createCustomStrategyTemplate === customStrategyTemplate) {
        updateCustomStrategies(updatedTargets);
      } else if (createCustomStrategyTemplate) {
        setItemNameAndTargets(createCustomStrategyTemplate, updatedTargets);
      }
      onSaveStrategy();
    }
  };

  return {
    handleSaveItem,
    createCustomStrategyLoading,
    editCustomStrategyLoading,
    createSubGroupCustomStrategyLoading,
    isCustomStrategyValid,
    targetsData,
  };
};

export default useSharedCreateCustomStrategy;
